import Phaser from 'phaser';
import { markTutorialSeen } from './save';

type TutorialKind = 'swap' | 'cascade';

export class TutorialOverlay extends Phaser.Scene {
  private kind: TutorialKind = 'swap';
  private fromX?: number;
  private fromY?: number;
  private toX?: number;
  private toY?: number;
  private dim?: Phaser.GameObjects.Graphics;
  private finger?: Phaser.GameObjects.Container;
  private fingerTween?: Phaser.Tweens.Tween;
  private dismissed = false;

  constructor() {
    super('TutorialOverlay');
  }

  init(data: { kind: TutorialKind; fromX?: number; fromY?: number; toX?: number; toY?: number }) {
    this.kind = data.kind;
    this.fromX = data.fromX;
    this.fromY = data.fromY;
    this.toX = data.toX;
    this.toY = data.toY;
    this.dismissed = false;
  }

  create() {
    const w = this.scale.width;
    const h = this.scale.height;

    this.dim = this.add.graphics();
    this.dim.fillStyle(0x000000, 0.42);
    this.dim.fillRect(0, 0, w, h);

    if (this.kind === 'swap') {
      this.drawSwapFinger(w, h);
      this.game.events.once('tutorial:swap-success', () => this.dismiss());
    } else {
      this.drawCascadeHint(w, h);
      this.game.events.once('tutorial:cascade', () => this.dismiss());
    }

    this.scale.on('resize', () => this.relayout());
  }

  private drawSwapFinger(w: number, h: number) {
    const hasHint =
      this.fromX !== undefined &&
      this.fromY !== undefined &&
      this.toX !== undefined &&
      this.toY !== undefined;
    const fromX = hasHint ? this.fromX! : Math.floor(w / 2 - 40);
    const fromY = hasHint ? this.fromY! : Math.floor(h * 0.64);
    const toX = hasHint ? this.toX! : Math.floor(w / 2 + 40);
    const toY = hasHint ? this.toY! : Math.floor(h * 0.64);
    const dist = Math.hypot(toX - fromX, toY - fromY);
    const handSize = Math.min(Math.max(dist * 0.8, 40), 90);

    const container = this.add.container(fromX, fromY);
    const handG = this.add.graphics();
    this.drawHandIcon(handG, handSize);
    container.add(handG);

    this.finger = container;

    this.fingerTween = this.tweens.add({
      targets: container,
      x: toX,
      y: toY,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawCascadeHint(w: number, h: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.5);
    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    g.lineStyle(4, 0xffd24a, 1);
    g.strokeRoundedRect(-90, -40, 180, 80, 12);
    container.add(g);

    const dots = this.add.graphics();
    for (let i = 0; i < 3; i++) {
      dots.fillStyle(0xffd24a, 1);
      dots.fillCircle(-40 + i * 40, 0, 8);
    }
    container.add(dots);

    this.finger = container;
    this.fingerTween = this.tweens.add({
      targets: container,
      scale: 1.08,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawHandIcon(g: Phaser.GameObjects.Graphics, size: number) {
    g.clear();
    const palm = size * 0.5;
    g.fillStyle(0xffffff, 0.95);
    g.lineStyle(3, 0x0a0f1f, 1);
    g.fillRoundedRect(-palm * 0.45, -palm * 0.1, palm * 0.9, palm, 6);
    g.strokeRoundedRect(-palm * 0.45, -palm * 0.1, palm * 0.9, palm, 6);
    const fingerW = palm * 0.22;
    g.fillRoundedRect(-fingerW / 2, -palm * 0.85, fingerW, palm * 0.9, 4);
    g.strokeRoundedRect(-fingerW / 2, -palm * 0.85, fingerW, palm * 0.9, 4);
    g.fillStyle(0xffd24a, 0.35);
    g.fillCircle(0, 0, palm * 0.85);
  }

  private relayout() {
    const w = this.scale.width;
    const h = this.scale.height;
    if (this.dim) {
      this.dim.clear();
      this.dim.fillStyle(0x000000, 0.42);
      this.dim.fillRect(0, 0, w, h);
    }
  }

  private dismiss() {
    if (this.dismissed) return;
    this.dismissed = true;
    markTutorialSeen(this.kind);
    this.fingerTween?.stop();
    this.tweens.add({
      targets: [this.dim, this.finger].filter(Boolean) as Phaser.GameObjects.GameObject[],
      alpha: 0,
      duration: 220,
      onComplete: () => this.scene.stop(),
    });
  }
}
